import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material"
import CircularProgress from "@mui/material/CircularProgress"
import { StyledButton } from "components/Button/StyledButton"
import { NormenImportInput } from "graphql/types"
import { ChangeEvent, useCallback, useEffect, useState } from "react"
import { colors } from "styles/colors"
import { treoLocationList } from "utils/constants"
import * as XLSX from "xlsx"

interface ImportNormsDialogProps {
  open: boolean
  onClose: () => void
  onImport: (input: NormenImportInput) => Promise<{ success: boolean; message: string }>
  isLoading?: boolean
}

const expectedTimeBasedHeaders = [
  "Norm",
  "Titel",
  "Jahr/Ausgabe",
  "Revision",
  "Branche",
  "Prüfverfahren",
  "Titel (LIMS intern)",
  "Angebotstext",
  "Personalbedarf [h]",
  "Anlagenbedarf [h]",
  "Stundensatz Prüfer",
  "Stundensatz Gerät",
  "Kosten Prüfer [h]",
  "durchschnittliche Kosten für Anlage [h]",
  "Tag (kommasepariert)",
  "Akkreditiert",
  "Standort",
]

const expectedConnectionBasedHeaders = [
  "Norm",
  "Titel",
  "Jahr/Ausgabe",
  "Revision",
  "Branche",
  "Prüfverfahren",
  "Titel (LIMS intern)",
  "Angebotstext",
  "Gehäuse",
  "1-Phasig AC",
  "3-Phasig AC",
  "DC",
  "Signal",
  "TK",
  "durchschnittliche Kosten für Anlage [h]",
  "Tag (kommasepariert)",
  "Akkreditiert",
  "Standort",
]

export const ImportNormsDialog = ({
  open,
  onClose,
  onImport,
  isLoading,
}: ImportNormsDialogProps) => {
  const [validationSummary, setValidationSummary] = useState<string | null>(null)

  const [fileName, setFileName] = useState<string | null>(null)
  const [hasErrors, setHasErrors] = useState<boolean>(false)
  const [parsedInput, setParsedInput] = useState<NormenImportInput | null>(null)
  const [showPartialImportPrompt, setShowPartialImportPrompt] = useState(false)
  const [importedExams, setImportedExams] = useState<string[]>([])
  const [missingExams, setMissingExams] = useState<string[]>([])
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [closeTimerId, setCloseTimerId] = useState<NodeJS.Timeout | null>(null)
  const [isProcessingFile, setIsProcessingFile] = useState<boolean>(false)

  const resetValues = useCallback(() => {
    if (closeTimerId) {
      clearInterval(closeTimerId)
      setCloseTimerId(null)
    }
    setValidationSummary(null)
    setFileName(null)
    setHasErrors(false)
    setParsedInput(null)
    setShowPartialImportPrompt(false)
    setImportedExams([])
    setMissingExams([])
    setSuccessMessage(null)
    setIsProcessingFile(false)
  }, [closeTimerId])

  useEffect(() => {
    if (!open) resetValues()
  }, [open, resetValues])

  const extractExaminations = (msg: string) => {
    const importedMatch = msg.match(/Importierbare Prüfverfahren:\s*((?:"[^"]+",?\s*)+)/)
    const missingMatch = msg.match(/Fehlende Prüfverfahren:\s*((?:"[^"]+",?\s*)+)/)

    const parseList = (s?: string) =>
      s ? (s.match(/"([^"]+)"/g)?.map((e) => e.replace(/^"|"$/g, "")) ?? []) : []

    return {
      imported: parseList(importedMatch?.[1]),
      missing: parseList(missingMatch?.[1]),
    }
  }

  const handleSuccessCloseWithMessage = (baseMsg: string) => {
    let countdown = 5
    setSuccessMessage(`${baseMsg} Dieses Fenster schließt in ${countdown} Sekunden.`)

    const intervalId = setInterval(() => {
      countdown -= 1
      if (countdown > 0) {
        setSuccessMessage(`${baseMsg} Dieses Fenster schließt in ${countdown} Sekunden.`)
      } else {
        clearInterval(intervalId)
        setCloseTimerId(null)
        setSuccessMessage(null)
        onClose()
      }
    }, 1000)

    setCloseTimerId(intervalId)
  }

  const handleImport = async () => {
    if (!parsedInput) return
    const result = await onImport(parsedInput)
    const { imported, missing } = extractExaminations(result.message)

    setImportedExams(imported)
    setMissingExams(missing)

    if (!result.success) {
      const isPartial = result.message.includes("Teilweise importiert")
      const isMissing = result.message.includes("Fehlende Prüfverfahren")
      setValidationSummary(result.message)
      setHasErrors(!isPartial)
      setShowPartialImportPrompt(isMissing && !parsedInput.allowPartialImport)
    } else {
      const baseMsg =
        imported.length > 0 && missing.length > 0
          ? "Teilweise importiert."
          : "Import war erfolgreich."
      handleSuccessCloseWithMessage(baseMsg)
    }
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    resetValues()

    setValidationSummary(null)
    setHasErrors(false)

    if (!file) return

    setFileName(file.name)
    setIsProcessingFile(true)

    const reader = new FileReader()
    reader.onload = (evt) => {
      const bstr = evt.target?.result
      const workbook = XLSX.read(bstr, { type: "binary" })

      const results: string[] = []
      let errorsFound = false

      const input: NormenImportInput = {
        timeBased: [],
        connectionBased: [],
      }

      workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName]
        const data = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        }) as string[][]

        const headers = data[0]?.map((h) => h?.trim()) ?? []
        const rows = data.slice(1).filter((row) => row.length > 0)

        const headerIndex: Record<string, number> = headers.reduce(
          (acc, h, i) => {
            acc[h] = i
            return acc
          },
          {} as Record<string, number>
        )

        const safeFloat = (value: string): number => {
          const parsed = parseFloat(value)
          return isNaN(parsed) ? 0 : parsed
        }

        const hasValidCoreFields = (row: string[], keys: string[]) => {
          return keys.some((k) => {
            const idx = headerIndex[k]
            return idx !== undefined && row[idx]?.trim()
          })
        }

        const splitTags = (value: string): string[] => {
          if (!value) return []
          return value
            .split(";")
            .map((t) => t.trim())
            .filter(Boolean)
        }

        if (sheetName === "TimeBased") {
          const missing = expectedTimeBasedHeaders.filter((h) => !(h in headerIndex))
          if (missing.length > 0) {
            results.push(
              `Fehler im Blatt "TimeBased": Es fehlen folgende Spalten:\n${missing.join(", ")}`
            )
            errorsFound = true
          } else {
            results.push(`[OK] Blatt "TimeBased" ist gültig.`)
            for (const row of rows) {
              if (!hasValidCoreFields(row, ["Norm", "Prüfverfahren", "Titel"])) {
                continue
              }

              const normName = row[headerIndex["Norm"]]?.trim()
              const examinationProcedure = row[headerIndex["Prüfverfahren"]]?.trim()

              input.timeBased?.push({
                name: normName,
                label: row[headerIndex["Titel"]],
                date: row[headerIndex["Jahr/Ausgabe"]],
                revision: row[headerIndex["Revision"]],
                branch: row[headerIndex["Branche"]],
                examinationProcedure: examinationProcedure,
                configName: row[headerIndex["Titel (LIMS intern)"]],
                offerText: row[headerIndex["Angebotstext"]],
                humanTimeHours: safeFloat(row[headerIndex["Personalbedarf [h]"]]),
                machineTimeHours: safeFloat(row[headerIndex["Anlagenbedarf [h]"]]),
                rateHuman: safeFloat(row[headerIndex["Stundensatz Prüfer"]]),
                rateMachine: safeFloat(row[headerIndex["Stundensatz Gerät"]]),
                costHuman: safeFloat(row[headerIndex["Kosten Prüfer [h]"]]),
                costMachine: safeFloat(row[headerIndex["durchschnittliche Kosten für Anlage [h]"]]),
                tags: splitTags(row[headerIndex["Tag (kommasepariert)"]]),
                isAccredited: row[headerIndex["Akkreditiert"]] === "Ja",
                location:
                  treoLocationList.find(
                    (location) => location.name === row[headerIndex["Standort"]].trim()
                  ) || null,
              })
            }
          }
        }

        if (sheetName === "ConnectionBased") {
          const missing = expectedConnectionBasedHeaders.filter((h) => !(h in headerIndex))
          if (missing.length > 0) {
            results.push(
              `Fehler im Blatt "ConnectionBased": Es fehlen folgende Spalten:\n${missing.join(", ")}`
            )
            errorsFound = true
          } else {
            results.push(`[OK] Blatt "ConnectionBased" ist gültig.`)
            for (const row of rows) {
              if (!hasValidCoreFields(row, ["Norm", "Prüfverfahren", "Titel"])) {
                continue
              }

              const normName = row[headerIndex["Norm"]]?.trim()
              const examinationProcedure = row[headerIndex["Prüfverfahren"]]?.trim()

              input.connectionBased?.push({
                name: normName,
                label: row[headerIndex["Titel"]],
                date: row[headerIndex["Jahr/Ausgabe"]],
                revision: row[headerIndex["Revision"]],
                branch: row[headerIndex["Branche"]],
                examinationProcedure: examinationProcedure,
                configName: row[headerIndex["Titel (LIMS intern)"]],
                offerText: row[headerIndex["Angebotstext"]],
                enclosure: safeFloat(row[headerIndex["Gehäuse"]]),
                ac1Phase: safeFloat(row[headerIndex["1-Phasig AC"]]),
                ac3Phase: safeFloat(row[headerIndex["3-Phasig AC"]]),
                dc: safeFloat(row[headerIndex["DC"]]),
                signal: safeFloat(row[headerIndex["Signal"]]),
                tk: safeFloat(row[headerIndex["TK"]]),
                costMachine: safeFloat(row[headerIndex["durchschnittliche Kosten für Anlage [h]"]]),
                tags: splitTags(row[headerIndex["Tag (kommasepariert)"]]),
                isAccredited: row[headerIndex["Akkreditiert"]] === "Ja",
                location:
                  treoLocationList.find(
                    (location) => location.name === row[headerIndex["Standort"]].trim()
                  ) || null,
              })
            }
          }
        }
      })

      setValidationSummary(results.join("\n\n"))
      setHasErrors(errorsFound)

      if (!errorsFound) {
        // Log processing summary for debugging
        const timeBasedCount = input.timeBased?.length || 0
        const connectionBasedCount = input.connectionBased?.length || 0
        const milCount = [
          ...(input.timeBased?.filter((tb) => tb.examinationProcedure?.includes("(MIL)")) || []),
          ...(input.connectionBased?.filter((cb) => cb.examinationProcedure?.includes("(MIL)")) ||
            []),
        ].length

        console.log(
          `📊 Excel parsing complete: ${timeBasedCount} TimeBased, ${connectionBasedCount} ConnectionBased, ${milCount} MIL procedures`
        )

        setParsedInput(input)
      } else {
        setParsedInput(null)
      }

      // File processing completed
      setIsProcessingFile(false)
    }

    reader.readAsArrayBuffer(file)
  }

  return isLoading || isProcessingFile ? (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      bgcolor="rgba(0, 0, 0, 0.7)"
      zIndex={1300}
    >
      <CircularProgress size={60} sx={{ mb: 2 }} />
      <Typography variant="h6" color="white" textAlign="center">
        {isProcessingFile ? "Excel-Datei wird verarbeitet..." : "Import läuft..."}
      </Typography>
      {isProcessingFile && (
        <Typography variant="body2" color="white" textAlign="center" sx={{ mt: 1 }}>
          Bitte warten Sie, während die Datei analysiert wird
        </Typography>
      )}
    </Box>
  ) : (
    <Dialog
      open={open}
      onClose={() => {
        resetValues()
        onClose()
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Normen importieren</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          {!successMessage && (
            <Button
              variant="contained"
              component="label"
              disabled={isProcessingFile}
              startIcon={isProcessingFile ? <CircularProgress size={20} /> : undefined}
            >
              {isProcessingFile ? "Datei wird verarbeitet..." : "Datei auswählen"}
              <input
                type="file"
                hidden
                accept=".xlsx"
                onChange={handleFileChange}
                onClick={(e) => {
                  ;(e.target as HTMLInputElement).value = ""
                }}
                disabled={isProcessingFile}
              />
            </Button>
          )}

          {fileName && !successMessage && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body2">
                {isProcessingFile ? "Verarbeitung läuft..." : "Datei ausgewählt:"}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {fileName}
              </Typography>
              {isProcessingFile && (
                <Box sx={{ mt: 1 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          )}

          {successMessage ? (
            <Typography variant="body2" sx={{ color: "success.main", mt: 2 }}>
              {successMessage}
            </Typography>
          ) : (
            <>
              {validationSummary && importedExams.length === 0 && missingExams.length === 0 && (
                <Box>
                  {validationSummary.split("\n\n").map((line, index) => {
                    const isOk = line.startsWith("[OK]")
                    const content = line.replace(/^\[OK\]\s*/, "")

                    return isOk ? (
                      <Box
                        key={index}
                        sx={{
                          border: `1px solid ${colors.button.save.background}`,
                          borderRadius: 1,
                          px: 2,
                          py: 1,
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: colors.button.save.background }}>
                          {content}
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        key={index}
                        sx={{
                          mb: 1,
                          border: "1px solid",
                          borderColor: colors.button.cancel.background,
                          borderRadius: 1,
                          px: 2,
                          py: 1,
                        }}
                      >
                        {content.includes("Es fehlen folgende Spalten:") ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                color: colors.errorText[400],
                                mb: 0.5,
                              }}
                            >
                              {content.split(":\n")[0]}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {content.split(":\n")[1]}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant="body2"
                            whiteSpace="pre-line"
                            color={colors.errorText[400]}
                          >
                            {content}
                          </Typography>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              )}

              {(importedExams.length > 0 || missingExams.length > 0) && (
                <Box mt={2} display="flex" flexDirection="column" gap={1}>
                  {importedExams.length > 0 && (
                    <Box
                      sx={{
                        border: `1px solid ${colors.button.save.background}`,
                        borderRadius: 1,
                        px: 2,
                        py: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: colors.button.save.background }}>
                        Importierbare Prüfverfahren:
                      </Typography>
                      <Typography variant="body2">{importedExams.join(", ")}</Typography>
                    </Box>
                  )}

                  {missingExams.length > 0 && (
                    <Box
                      sx={{
                        border: "1px solid",
                        borderColor: colors.errorText[400],
                        borderRadius: 1,
                        px: 2,
                        py: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: colors.errorText[400] }}>
                        Fehlende Prüfverfahren:
                      </Typography>
                      <Typography variant="body2">{missingExams.join(", ")}</Typography>
                    </Box>
                  )}

                  {importedExams.length > 0 && missingExams.length > 0 && (
                    <Typography variant="body2" color="warning.main" whiteSpace="pre-line">
                      Möchten Sie die Norm trotzdem teilweise importieren?
                    </Typography>
                  )}

                  {importedExams.length === 0 && missingExams.length > 0 && (
                    <Typography variant="body2" sx={{ color: colors.errorText[400] }}>
                      Norm kann nicht importiert werden, da alle Prüfverfahren fehlen.
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {!successMessage && (
          <StyledButton onClick={onClose} buttonType="cancel">
            Abbrechen
          </StyledButton>
        )}
        {!showPartialImportPrompt && !successMessage && (
          <Button
            onClick={handleImport}
            color="primary"
            variant="contained"
            disabled={hasErrors || !parsedInput || successMessage !== null || isProcessingFile}
          >
            Importieren
          </Button>
        )}
        {showPartialImportPrompt && parsedInput && importedExams.length > 0 && !successMessage && (
          <Button
            variant="outlined"
            color="warning"
            onClick={async () => {
              const retryInput = {
                ...parsedInput,
                allowPartialImport: true,
              }
              const retryResult = await onImport(retryInput)
              if (retryResult.success) {
                setShowPartialImportPrompt(false)
                const { imported, missing } = extractExaminations(retryResult.message)
                setImportedExams(imported)
                setMissingExams(missing)
                const baseMsg =
                  imported.length > 0 && missing.length > 0
                    ? "Teilweise importiert."
                    : "Import war erfolgreich."
                handleSuccessCloseWithMessage(baseMsg)
              } else {
                setValidationSummary(retryResult.message)
              }
            }}
          >
            Teilweise importieren
          </Button>
        )}
        {successMessage && (
          <Button
            onClick={() => {
              resetValues()
              onClose()
            }}
            color="secondary"
            variant="outlined"
          >
            Schließen
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
